import { Dialog, DialogContent } from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Typography } from '@neo/primitives/typography'
import {
  getConnectedEdges,
  type Node,
  useNodes,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react'
import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { v4 } from 'uuid'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { AgentChannels, FlowSourceTypes, type FlowTypes } from 'v2/types'
import { logger } from 'v2/utils/logger'
import { PATH_PICKER_SURFACE, PathPicker } from '../components/path-picker'
import { isFlowBlockedNode } from '../config/flow-blocked-node-types'
import { MenuNodeID } from '../config/nodes.config'
import {
  AI_NODE_TYPES,
  FlowNodeIconClass,
  type NodeComponent,
  nodeSections,
} from '../config/panel.config'
import { FlowNodeDescriptions } from '../constants'
import { useFlowSection } from '../flow-section'
import { qnrView } from '../sidebar/queue-and-route/embed-view'
import { useFlowStore } from '../store'
import {
  type CallTriggerPromptData,
  FlowNodeTypes,
  type NodeDataBase,
  type OutputState,
} from '../types'
import {
  clickNode,
  getChannelTypeFromFlowType,
  getChildNodePosition,
  getDefaultNodeData,
  getNodeVarsForHandle,
} from '../utils'
import {
  createAndAddIntentToAiNode,
  hasCallTriggerUpstream,
  isAiAgentOrActionNode,
  isCallAiSourceNode,
} from '../utils/ai-agent-utils'
import { isAiAgentV2Type } from '../utils/ai-agent-v29'
import { connectChildNode } from '../utils/node-connect'
import { getOutputStates } from '../utils/output-states'
import { pendingEdgeSection } from '../utils/pending-edge'
import { PanelItem } from './panel-item'

export const MenuItems = ({
  isDrag,
  onSelect,
  opStates: providedOpStates,
  pathsTitle,
  currentNodeType,
  sourceNodeId,
  open,
  onOpenChange,
  isStandalone = false, // New prop for standalone node creation
}: {
  isDrag?: boolean
  onSelect?: (nodeId: string) => void
  // Unconnected paths to offer in the path-selection step. When omitted, they
  // are derived from the source node (drag-to-canvas case).
  opStates?: OutputState[]
  pathsTitle?: string
  currentNodeType?: FlowNodeTypes
  sourceNodeId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  isStandalone?: boolean // New prop for standalone node creation
}) => {
  const [search, setSearch] = useState('')
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const shouldSkipPathSelection =
    isStandalone ||
    currentNodeType === FlowNodeTypes.start ||
    isAiAgentOrActionNode(currentNodeType)
  const updateInternal = useUpdateNodeInternals()

  const { updateNode, updateEdge, getNodes, getNode, addNodes, addEdges, getEdges, getEdge } =
    useReactFlow<Node<NodeDataBase<unknown>>>()
  const { isLivekitEnabled } = useLivekitEnabled()
  const { isAINodeDisabled, aiAgentV2_5Enabled, streamNodeEnabled } = useFeatureFlags()
  const { allowsAi } = useFlowSection()
  const nodes = useNodes()

  const shouldHideAiAction = useMemo(() => {
    if (!isLivekitEnabled || !sourceNodeId) return false
    return isCallAiSourceNode(nodes.find((n) => n.id === sourceNodeId))
  }, [isLivekitEnabled, sourceNodeId, nodes])

  const filteredSections = useMemo(() => {
    const searchTokens = search.toLowerCase().trim().split(/\s+/).filter(Boolean)

    const isNodeVisible = (node: NodeComponent) => {
      if (isAINodeDisabled && AI_NODE_TYPES.includes(node.meta.node_type)) return false
      if (!allowsAi && isFlowBlockedNode(node.meta.node_type)) return false
      if (shouldHideAiAction && node.meta.node_type === FlowNodeTypes.ai_action) return false
      // agent_node is livekit-backed and voice-only, and rolls out behind its
      // own flag: both gates must hold for the picker entry to show.
      if (
        node.meta.node_type === FlowNodeTypes.agent_node &&
        (!isLivekitEnabled || !aiAgentV2_5Enabled)
      )
        return false
      // Stream is behind its own rollout flag; existing flows with a stream node
      // still open and render when it is off.
      if (node.meta.node_type === FlowNodeTypes.stream && !streamNodeEnabled) return false
      // Screening runs only on the LiveKit runtime — hide from non-LiveKit orgs.
      if (node.meta.node_type === FlowNodeTypes.outbound_screening && !isLivekitEnabled)
        return false
      if (node.meta.node_type === FlowNodeTypes.queue_and_route && !qnrView().inPalette)
        return false
      // Embed mode never loads the connected apps, so the node could not be set up there.
      if (node.meta.node_type === FlowNodeTypes.mcp_tool && isEmbedMode()) return false
      if (searchTokens.length) {
        const haystack = [
          node.label,
          FlowNodeDescriptions[node.meta.node_type],
          ...(node.keywords ?? []),
        ]
          .join(' ')
          .toLowerCase()
        if (!searchTokens.every((token) => haystack.includes(token))) return false
      }
      return true
    }

    // Single pass: filter each section's nodes, keeping only non-empty sections.
    return nodeSections.flatMap((section) => {
      const nodes = section.nodes.filter(isNodeVisible)
      return nodes.length ? [{ ...section, nodes }] : []
    })
  }, [
    search,
    shouldHideAiAction,
    isAINodeDisabled,
    allowsAi,
    isLivekitEnabled,
    aiAgentV2_5Enabled,
    streamNodeEnabled,
  ])

  // Unconnected paths for the path-selection step: either the caller-provided
  // list or derived from the dragged/opened node. A drag from a section pill
  // carries the section in the menu edge's pending handle — scope to it.
  const { pathStates, pathsHeading } = useMemo(() => {
    if (providedOpStates) return { pathStates: providedOpStates, pathsHeading: pathsTitle }

    // Simplified logic: use sourceNodeId directly when available (for isDrag case)
    const menuEdge = isDrag ? getEdge(MenuNodeID) : undefined
    const sourceId = sourceNodeId || menuEdge?.source || ''
    const node = getNode(sourceId)
    if (!node) return { pathStates: [], pathsHeading: pathsTitle }

    const outStates = getOutputStates({
      data: node.data.config as NodeDataBase['config'],
      type: node.type as FlowNodeTypes,
    })

    const outGoers = getConnectedEdges([node], getEdges())
    const unConnected = outStates.filter(
      (state) =>
        !outGoers.some((edge) => edge.sourceHandle === state.id && edge.source === node.id),
    )
    const section = pendingEdgeSection(menuEdge?.sourceHandle)
    if (!section) return { pathStates: unConnected, pathsHeading: pathsTitle }
    return {
      pathStates: unConnected.filter((state) => state.section === section),
      pathsHeading: pathsTitle ?? `${section} — Select a path`,
    }
  }, [isDrag, getEdge, getNode, getEdges, providedOpStates, pathsTitle, sourceNodeId])

  // Captures where the AI conversation would have been added so the confirmation
  // dialog can build the source -> Initiate Call -> AI conversation chain there.
  const resolveCallTriggerPrompt = (): CallTriggerPromptData => {
    const sourceNode = sourceNodeId ? getNode(sourceNodeId) : undefined
    if (isStandalone || !sourceNodeId || !sourceNode) {
      return { isStandalone: true, isDrag: false, sourceHandle: '', position: { x: 0, y: 0 } }
    }

    if (isDrag) {
      const menuEdge = getEdge(MenuNodeID)
      const originalHandle =
        shouldSkipPathSelection && menuEdge ? menuEdge.sourceHandle : selectedPath
      return {
        sourceNodeId,
        isStandalone: false,
        isDrag: true,
        sourceHandle: originalHandle || '',
        position: getNode(MenuNodeID)?.position ?? { x: 0, y: 0 },
      }
    }

    const sourceHandleId =
      currentNodeType === FlowNodeTypes.start
        ? FlowSourceTypes[useFlowStore.getState().currentFlowType as FlowTypes]
        : ''

    return {
      sourceNodeId,
      isStandalone: false,
      isDrag: false,
      sourceHandle: selectedPath || sourceHandleId,
      position: getChildNodePosition(sourceNode, getNodes(), getEdges()),
    }
  }

  const createNodeFromMenu = (subItem: NodeComponent) => {
    if (!selectedPath && !shouldSkipPathSelection) return
    if (!subItem.meta.node_type) return

    // An AI conversation resolves to the call channel in voice/non-messaging flows.
    // A call AI node needs an active call upstream: a Voice Call trigger (inbound
    // call flow) or an Initiate Call node (outbound). When neither exists, prompt
    // to add an Initiate Call (chained before the AI conversation) instead.
    if (
      isAiAgentV2Type(subItem.meta.node_type) ||
      subItem.meta.node_type === FlowNodeTypes.agent_node
    ) {
      const flowType = useFlowStore.getState().currentFlowType
      const wouldBeCallChannel =
        !!flowType && getChannelTypeFromFlowType(flowType as FlowTypes) === AgentChannels.call
      if (
        wouldBeCallChannel &&
        !hasCallTriggerUpstream(sourceNodeId, getNodes(), getEdges(), flowType as FlowTypes)
      ) {
        useFlowStore.getState().setCallTriggerPrompt({
          ...resolveCallTriggerPrompt(),
          nodeType: subItem.meta.node_type,
        })
        onOpenChange(false)
        return
      }
    }

    // Handle standalone node creation (for the new Add Node button)
    if (isStandalone) {
      const newNodeId = v4()
      const candidateNodeData = {
        id: newNodeId,
        type: subItem.meta.node_type,
        position: { x: 0, y: 0 },
        data: getDefaultNodeData(subItem.meta.node_type, getNodes),
      } as Node<NodeDataBase<unknown>>

      // Set the candidate node for mouse following
      useFlowStore.getState().setCandidateNode(candidateNodeData)
      onOpenChange(false) // Close the modal
      return
    }

    if (isDrag && sourceNodeId) {
      const newNodeId = v4()
      let intentId = ''

      updateNode(MenuNodeID, {
        id: newNodeId,
        zIndex: 0, // or else it will be above the menu component
        type: subItem.meta.node_type,
        data: getDefaultNodeData(subItem.meta.node_type, getNodes),
      })

      const sourceNode = getNode(sourceNodeId || MenuNodeID) as Node<NodeDataBase<unknown>>
      if (isAiAgentOrActionNode(sourceNode.type)) {
        intentId = createAndAddIntentToAiNode(sourceNodeId) || ''
        if (!intentId) {
          logger.error('Failed to create intent for AI node')
          return
        }
      }
      updateInternal(sourceNodeId)

      updateEdge(MenuNodeID, () => {
        // Use sourceNodeId directly instead of edge.source

        // Handle AI nodes - create intent and update source node data

        // Feature: Nodevars
        // For nodes that skip path selection (like Start), use the original handle from the menu edge
        const menuEdge = getEdge(MenuNodeID)
        const originalHandle =
          shouldSkipPathSelection && menuEdge ? menuEdge.sourceHandle : selectedPath

        const nodeVars = getNodeVarsForHandle(sourceNode, originalHandle || '')

        // Determine the correct sourceHandle based on source node type
        const sourceHandle = isAiAgentOrActionNode(sourceNode.type)
          ? intentId
          : originalHandle || ''

        return {
          id: v4(),
          target: newNodeId,
          sourceHandle,
          data: { nodeVars },
        }
      })
      clickNode(newNodeId) // open the sidebar
    } else {
      const sourceHandleId =
        currentNodeType === FlowNodeTypes.start
          ? FlowSourceTypes[useFlowStore.getState().currentFlowType as FlowTypes]
          : ''
      let newNodeId: string | undefined
      const sourceNode = sourceNodeId ? getNode(sourceNodeId) : undefined
      if (sourceNode) {
        const newNode = connectChildNode({
          sourceNode,
          outputHandle: selectedPath || sourceHandleId,
          nodeType: subItem.meta.node_type,
          position: getChildNodePosition(sourceNode, getNodes(), getEdges()),
          getNodes,
          addNodes,
          addEdges,
          updateNodeInternals: updateInternal,
        })
        if (!newNode) return
        newNodeId = newNode.id
      }
      if (newNodeId) {
        clickNode(newNodeId)
        onSelect?.(newNodeId)
      }
    }
  }

  if (!selectedPath && !shouldSkipPathSelection)
    return (
      <div className={PATH_PICKER_SURFACE}>
        <PathPicker
          opStates={pathStates}
          title={pathsHeading}
          onSelect={(state) => setSelectedPath(state.id)}
        />
      </div>
    )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-menu-items" className="w-[600px] max-w-none">
        <Typography variant="H4/500">Select Node</Typography>
        <Input
          placeholder="Search"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9"
          startIcon={Search}
        />

        <ScrollArea className="h-[450px] pr-4 -mr-4">
          <div className="flex flex-col gap-6">
            {filteredSections.map((section) => (
              <div key={section.title}>
                <div className="font-jetbrains text-label uppercase text-muted-foreground mb-3 ml-1">
                  {section.title}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                  {section.nodes.map((node) => {
                    const nodeConfig = FlowNodeIconClass[node.meta.node_type]
                    return (
                      <PanelItem
                        key={node.id}
                        node={node}
                        icon={nodeConfig.icon}
                        iconClass={nodeConfig.class}
                        createNodeFromMenu={createNodeFromMenu}
                      />
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
